import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * Everything inside the dialog that a Tab can land on.
 *
 * `:not([disabled])` and the negative-tabindex exclusion matter: a disabled button or an
 * element deliberately removed from the tab order must not become the trap's boundary, or
 * Tab appears to stick on nothing.
 */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * GlassModal
 * A reusable glassmorphism-styled modal wrapper for consistent overlays/panels.
 *
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - header?: ReactNode
 * - footer?: ReactNode
 * - children: ReactNode (modal body)
 * - maxWidth?: string (Tailwind class; e.g., 'max-w-2xl', 'max-w-4xl')
 * - position?: 'center' | 'top' (default 'center')
 * - topOffset?: string (Tailwind spacing; used when position='top', default 'top-16')
 * - closeOnOverlay?: boolean (default true)
 * - overlayClassName?: string
 * - containerClassName?: string
 */
const GlassModal = ({
  isOpen,
  onClose,
  header,
  footer,
  children,
  maxWidth = 'max-w-2xl',
  position = 'center',
  topOffset = 'top-16',
  closeOnOverlay = true,
  overlayClassName = '',
  containerClassName = '',
}) => {
  const panelRef = useRef(null);
  // Declared HERE, above the `if (!isOpen) return null` further down: a hook after an early
  // return breaks react-hooks/rules-of-hooks, which is error-level and fails the build. See
  // the dismissal note further down for what this is for (board #426).
  const pressStartedOnOverlay = useRef(false);

  /**
   * FOCUS TRAP. This component already claims `aria-modal="true"`, which tells assistive tech
   * that everything outside the dialog is inert — but focus could Tab straight out into the
   * page behind, so the claim was false. That is worse than having no trap at all: a screen
   * reader user is told the background is unavailable and then lands in it.
   *
   * Reported independently as "modal does not grab focus on open, and Tab escapes the dialog
   * into the page behind". GlassModal is used by 57 files, so it is fixed once here rather
   * than per modal.
   */
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose?.();
      return;
    }
    if (e.key !== 'Tab' || !panelRef.current) return;

    const focusable = Array.from(panelRef.current.querySelectorAll(FOCUSABLE))
      // offsetParent is null for anything display:none — a collapsed section's fields must
      // not become invisible stops in the cycle.
      .filter((el) => el.offsetParent !== null || el === document.activeElement);

    if (focusable.length === 0) {
      // Nothing to focus, but the dialog still must not leak Tab to the page behind it.
      e.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    // Wrap at both ends, and also catch the case where focus is somehow already outside.
    if (e.shiftKey && (active === first || !panelRef.current.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || !panelRef.current.contains(active))) {
      e.preventDefault();
      first.focus();
    }
  };

  /**
   * THE HANDLER IS READ THROUGH A REF, AND THAT IS THE WHOLE POINT.
   *
   * This effect used to list `handleKeyDown` in its dependencies, and `handleKeyDown` was a
   * useCallback keyed on `onClose`. Almost every one of the 57 call sites passes an inline
   * arrow — `onClose={() => setShowModal(false)}` — or a plain function declared in the
   * component body, so `onClose` gets a NEW identity on every render of the parent.
   *
   * A controlled input re-renders its parent on every keystroke. So: type a character →
   * parent re-renders → new `onClose` → new `handleKeyDown` → this effect's deps changed →
   * React runs the CLEANUP, which calls `previouslyFocused.focus()` and throws focus back
   * OUT of the dialog, then re-runs the body, whose timer focuses the FIRST control in the
   * panel. One character lands and the caret is gone.
   *
   * Nothing unmounted and no blur handler was involved, which is why the typed value survived
   * and why it did not look like a remount. It was the focus-restoring cleanup of an effect
   * that had no business re-running. Testers hit it as "only the first character of each
   * field is saved" — `i`/`e`, `O`/`O`/`A` — because a machine types without re-clicking.
   *
   * Keyed on `isOpen` alone now: the listener attaches once per open and always calls the
   * latest handler. Do not add the handler back to the dependency array.
   */
  const handleKeyDownRef = useRef(handleKeyDown);
  useEffect(() => {
    handleKeyDownRef.current = handleKeyDown;
  });

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => handleKeyDownRef.current(e);
    document.addEventListener('keydown', onKeyDown);
    // Mark body so floating UI (e.g., Report-a-Problem FAB) can hide while
    // any modal is open. Reference-counted via a data attribute so nested
    // modals don't prematurely clear the flag when one closes.
    const count = parseInt(document.body.dataset.openModals || '0', 10);
    document.body.dataset.openModals = String(count + 1);

    // Remember where focus came FROM so it can be put back — otherwise closing a dialog
    // drops the keyboard user at the top of the document and they have to tab back to
    // wherever they were.
    const previouslyFocused = document.activeElement;

    // Move focus INTO the dialog. First real control if there is one, otherwise the panel
    // itself (which carries tabIndex={-1} for exactly this). Deferred a frame because the
    // children have not been laid out when this effect first runs.
    const focusTimer = setTimeout(() => {
      if (!panelRef.current) return;
      const firstFocusable = panelRef.current.querySelector(FOCUSABLE);
      (firstFocusable || panelRef.current).focus?.();
    }, 0);

    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKeyDown);
      const c = parseInt(document.body.dataset.openModals || '1', 10);
      document.body.dataset.openModals = String(Math.max(0, c - 1));
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  // Fix: solid-ish overlay so underlying page doesn't bleed through the frosted panel
  const baseOverlay = `fixed inset-0 z-50 p-6 bg-slate-900/70 backdrop-blur-md`;
  const centerLayout = 'flex items-center justify-center';
  const topLayout = 'overflow-y-auto';

  const overlayClasses = [
    baseOverlay,
    position === 'center' ? centerLayout : topLayout,
    overlayClassName,
  ].join(' ');

  // Fix: high panel opacity (was bg-white/10 → caused the documented "glare"/bleed-through)
  const baseContainer = `w-full ${maxWidth} max-h-[90vh] overflow-hidden flex flex-col rounded-2xl bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl border border-white/20 shadow-2xl ring-1 ring-white/10`;
  const topContainer = `relative ${topOffset} mx-auto`;

  const containerClasses = [
    baseContainer,
    position === 'top' ? topContainer : '',
    containerClassName,
  ].join(' ');

  /**
   * Board #426. Dismissal used to need only that the CLICK landed on the overlay — but a
   * `click` fires on the nearest common ancestor of where the press started and where it
   * ended. So dragging right-to-left across the recipient field to select the email, and
   * releasing a few pixels past its left edge, produced a click whose target was the overlay,
   * and the dialog vanished mid-selection. Jerry hit it twice on the quote Send modal.
   *
   * Requiring the press to have STARTED on the overlay too makes dismissal an intentional
   * gesture rather than an accident of where a drag happened to end. Clicking the backdrop
   * still closes; selecting text in either direction no longer does.
   *
   * This is GlassModal, so it is every dialog in the app — text selection inside a field is
   * not a quote-specific act.
   */
  const onOverlayMouseDown = (e) => {
    pressStartedOnOverlay.current = e.target === e.currentTarget;
  };

  const onOverlayClick = (e) => {
    const startedOnOverlay = pressStartedOnOverlay.current;
    pressStartedOnOverlay.current = false;
    if (!closeOnOverlay) return;
    if (startedOnOverlay && e.target === e.currentTarget) onClose?.();
  };

  // Portal to <body> so the fixed overlay is positioned against the VIEWPORT.
  // Rendered inline, an ancestor with transform/filter/backdrop-filter (the page's
  // backdrop-blur containers) becomes the containing block for `position: fixed`,
  // centering the modal in the middle of the long page — below the fold — so it
  // reads as "failed to load" until you scroll. The portal escapes that subtree.
  // role/aria-modal live on the PANEL, not the full-screen overlay. The overlay is the
  // backdrop; naming it the dialog tells assistive tech the dialog includes the dimmed page
  // area around it. tabIndex={-1} makes the panel programmatically focusable, so there is
  // somewhere to put focus when a dialog has no controls of its own.
  //
  // This comment is `//` and sits ABOVE the return on purpose: a {/* */} block directly after
  // `return createPortal(` is an expression position, where `{` opens an object literal and
  // the file stops parsing. CLAUDE.md §15 calls this out by name.
  return createPortal(
    <div className={overlayClasses} onMouseDown={onOverlayMouseDown} onClick={onOverlayClick}>
      <div className={containerClasses} ref={panelRef} role="dialog" aria-modal="true" tabIndex={-1}>
        {header && (
          <div className="flex items-center justify-between p-6 border-b border-white/10 sticky top-0 bg-white/5 backdrop-blur-sm z-10">
            {header}
          </div>
        )}

        <div className="p-6 overflow-y-auto flex-1 min-h-0">
          {children}
        </div>

        {footer && (
          <div className="flex items-center justify-end gap-3 p-6 border-t border-white/10 bg-white/5 backdrop-blur-sm sticky bottom-0">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default GlassModal;

